import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from 'react';

import { useAuth } from '../../auth/Auth';
import {
  getNotifications,
  getSeenNotificationCount,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  markAllNotificationsAsSeen,
  markNotificationAsReadById,
} from '../../auth/_requests';
import { Notification } from '../../helpers/utils/types';
import NotificationModal from '../../pages/sse/NotificationModal';

interface NotificationListenerContextType {
  notifications?: Notification[];
  unreadNotificationCount?: number;
  seenNotificationCount?: number;
  markAsSeen: () => void;
  markAllAsRead: () => void;
  markNotificationAsRead: (notificationId: string) => void;
  fetchMoreNotifications: () => void;
  hasMore: boolean;
  resetPagination: () => void;
  refreshSeenCount: () => void;
}

const NotificationListenerContext = createContext<NotificationListenerContextType | undefined>(
  undefined
);

export const NotificationListenerProvider = ({ children }: { children: ReactNode }) => {
  const { auth } = useAuth();
  const token = auth?.data?.token;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [seenNotificationCount, setSeenNotificationCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchAllNotifications = useCallback(
    async (page: number, limit: number, append = false) => {
      try {
        const [notifRes, countRes, seenCountRes] = await Promise.all([
          getNotifications(token, page, limit),
          getUnreadNotificationCount(token),
          getSeenNotificationCount(token),
        ]);

        const newNotifs = notifRes?.data || [];

        setNotifications((prev) => (append ? [...prev, ...newNotifs] : newNotifs));
        setHasMore(newNotifs.length === limit);
        setUnreadNotificationCount(countRes?.count || 0);
        setSeenNotificationCount(seenCountRes?.count || 0);
      } catch (error) {
        console.error('Error fetching notifications or count:', error);
      }
    },
    [token]
  );

  const refreshSeenCount = useCallback(async () => {
    try {
      const seenCountRes = await getSeenNotificationCount(token);
      setSeenNotificationCount(seenCountRes?.count || 0);
    } catch (error) {
      console.error('Error refreshing seen count:', error);
    }
  }, [token]);

  const fetchMoreNotifications = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchAllNotifications(nextPage, 5, true);
  };

  const resetPagination = () => {
    setPage(1);
    fetchAllNotifications(1, 5);
  };

  const markAsSeen = useCallback(async () => {
    try {
      await markAllNotificationsAsSeen(token);
      fetchAllNotifications(1, 5);
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }
  }, [token, fetchAllNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead(token);
      fetchAllNotifications(1, 5);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [token, fetchAllNotifications]);

  const markNotificationAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markNotificationAsReadById(token, notificationId);
        fetchAllNotifications(1, 5);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [token, fetchAllNotifications]
  );

  useEffect(() => {
    if (token) {
      fetchAllNotifications(1, 5);

      intervalRef.current = setInterval(() => {
        fetchAllNotifications(1, 5);
      }, 60 * 1000); // 60 seconds
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [token, fetchAllNotifications]);

  return (
    <NotificationListenerContext.Provider
      value={{
        notifications,
        unreadNotificationCount,
        seenNotificationCount,
        markAsSeen,
        markAllAsRead,
        markNotificationAsRead,
        fetchMoreNotifications,
        hasMore,
        resetPagination,
        refreshSeenCount,
      }}
    >
      <>
        {children}
        <NotificationModal
          message={''} // optional: you can remove this if modal isn't needed
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      </>
    </NotificationListenerContext.Provider>
  );
};

export const useNotificationListener = () => {
  const context = useContext(NotificationListenerContext);
  if (!context) {
    throw new Error('useNotificationListener must be used within a NotificationListenerProvider');
  }
  return context;
};
